import React, { useCallback, useState } from "react";

import "./JsonViewer.scss";

interface JsonViewerProps {
	data: any;
	name?: string;
	collapsed?: boolean;
	depth?: number;
	maxDepth?: number;
}

const JsonViewer: React.FC<JsonViewerProps> = ({
	data,
	name = "",
	collapsed = false,
	depth = 0,
	maxDepth = 10,
}) => {
	const [isCollapsed, setIsCollapsed] = useState(collapsed);

	const handleToggle = useCallback(() => {
		setIsCollapsed((prev) => !prev);
	}, []);

	// Prevent infinite recursion
	if (depth > maxDepth) {
		return <span className="json-viewer__max-depth">...</span>;
	}

	// Handle null and undefined
	if (data === null) {
		return <span className="json-viewer__null">null</span>;
	}

	if (data === undefined) {
		return <span className="json-viewer__undefined">undefined</span>;
	}

	// Handle primitive types
	if (typeof data === "string") {
		return <span className="json-viewer__string">"{data}"</span>;
	}

	if (typeof data === "number") {
		return <span className="json-viewer__number">{data}</span>;
	}

	if (typeof data === "boolean") {
		return <span className="json-viewer__boolean">{data.toString()}</span>;
	}

	// Handle functions
	if (typeof data === "function") {
		return <span className="json-viewer__function">[Function]</span>;
	}

	// Handle arrays
	if (Array.isArray(data)) {
		if (data.length === 0) {
			return <span className="json-viewer__array-empty">[]</span>;
		}

		return (
			<div className="json-viewer__array">
				<button className="json-viewer__toggle" onClick={handleToggle} aria-expanded={!isCollapsed}>
					<span className={`json-viewer__toggle-icon ${isCollapsed ? "collapsed" : "expanded"}`}>
						▶
					</span>
					{name && <span className="json-viewer__key">{name}: </span>}
					<span className="json-viewer__bracket">[</span>
					{isCollapsed && (
						<span className="json-viewer__preview">
							{data.length} item{data.length !== 1 ? "s" : ""}
						</span>
					)}
					{isCollapsed && <span className="json-viewer__bracket">]</span>}
				</button>
				{!isCollapsed && (
					<div className="json-viewer__content">
						{data.map((item, index) => (
							<div key={index} className="json-viewer__array-item">
								<JsonViewer
									data={item}
									name={index.toString()}
									depth={depth + 1}
									maxDepth={maxDepth}
								/>
								{index < data.length - 1 && <span className="json-viewer__comma">,</span>}
							</div>
						))}
						<span className="json-viewer__bracket">]</span>
					</div>
				)}
			</div>
		);
	}

	// Handle objects
	if (typeof data === "object") {
		const keys = Object.keys(data);

		if (keys.length === 0) {
			return <span className="json-viewer__object-empty">{"{}"}</span>;
		}

		return (
			<div className="json-viewer__object">
				<button className="json-viewer__toggle" onClick={handleToggle} aria-expanded={!isCollapsed}>
					<span className={`json-viewer__toggle-icon ${isCollapsed ? "collapsed" : "expanded"}`}>
						▶
					</span>
					{name && <span className="json-viewer__key">{name}: </span>}
					<span className="json-viewer__bracket">{"{"}</span>
					{isCollapsed && (
						<span className="json-viewer__preview">
							{keys.length} key{keys.length !== 1 ? "s" : ""}
						</span>
					)}
					{isCollapsed && <span className="json-viewer__bracket">{"}"}</span>}
				</button>
				{!isCollapsed && (
					<div className="json-viewer__content">
						{keys.map((key, index) => (
							<div key={key} className="json-viewer__object-item">
								<span className="json-viewer__key">{key}:</span>
								<JsonViewer data={data[key]} depth={depth + 1} maxDepth={maxDepth} />
								{index < keys.length - 1 && <span className="json-viewer__comma">,</span>}
							</div>
						))}
						<span className="json-viewer__bracket">{"}"}</span>
					</div>
				)}
			</div>
		);
	}

	// Fallback for unknown types
	return <span className="json-viewer__unknown">{String(data)}</span>;
};

export default JsonViewer;
